import { useState } from "react";
import { BpmOverrideDialog } from "@/components/bpm/BpmOverrideDialog";
import { MarketValueDialog } from "@/components/valuation/MarketValueDialog";
import {
  BpmDialog,
  CostDialog,
  ScoreDialog,
  ValuationDialog,
} from "@/components/explain/ExplainDialogs";
import { OpportunityTableRow } from "@/components/opportunities/OpportunityTableRow";
import { OpportunityMobileCard } from "@/components/opportunities/OpportunityMobileCard";
import { useAnalysisDetail } from "@/lib/useAnalysisDetail";

export const SORT_OPTIONS = [
  { key: "score", label: "Deal Score (hoog → laag)" },
  { key: "contribution", label: "Hoogste handelsbijdrage" },
  { key: "roi", label: "Hoogste ROI" },
  { key: "price", label: "Laagste inkoopprijs" },
  { key: "bpm", label: "Laagste BPM" },
  { key: "newest", label: "Nieuwste advertentie" },
  { key: "mileage", label: "Laagste kilometerstand" },
  { key: "confidence", label: "Hoogste waarderingsbetrouwbaarheid" },
];

export function sortAnalyses(rows, sortKey) {
  const value = (a) => {
    const h = a.headline || {};
    const v = a.vehicle_snapshot || {};
    switch (sortKey) {
      case "contribution":
        return -(h.contribution ?? -Infinity);
      case "roi":
        return -(h.roi_pct ?? -Infinity);
      case "price":
        return v.price ?? Infinity;
      case "bpm":
        return h.bpm ?? Infinity;
      case "newest":
        return -(new Date(a.timestamps?.analysis_created || 0).getTime() || 0);
      case "mileage":
        return v.mileage_km ?? Infinity;
      case "confidence":
        return -(h.confidence_pct ?? -Infinity);
      default:
        return -(h.deal_score ?? -Infinity);
    }
  };
  return [...rows].sort((a, b) => value(a) - value(b));
}

const COMPACT_COLUMNS = [
  "Voertuig", "Land", "Inkoop", "BPM", "Landed", "NL waarde", "Bijdrage", "ROI", "Score", "Betr.", "",
];
const FULL_COLUMNS = [
  "Voertuig", "1e toelating", "KM", "Land", "Verkoper", "Bron", "Vraagprijs", "BTW", "BPM",
  "Transport", "Import/kosten", "Landed cost", "Verwacht NL", "Bijdrage", "ROI", "Deal Score", "",
];

export const OpportunityTable = ({
  rows,
  compact = false,
  testid = "opportunity-table",
  onChanged,
}) => {
  const [dialog, setDialog] = useState({ type: null, row: null });
  const [overrideRow, setOverrideRow] = useState(null);
  const [valueRow, setValueRow] = useState(null);
  const [expanded, setExpanded] = useState({});

  const close = () => setDialog({ type: null, row: null });
  const active = dialog.row;
  const columns = compact ? COMPACT_COLUMNS : FULL_COLUMNS;

  const detailListingId = valueRow?.listing_id ?? active?.listing_id ?? null;
  const detailEnabled =
    valueRow !== null || ["bpm", "valuation", "score", "cost"].includes(dialog.type);
  const { detail, loading, patch } = useAnalysisDetail(detailListingId, detailEnabled);
  const detailFor = (row) =>
    detail && row && detail.listing_id === row.listing_id ? detail : null;
  const dialogChanged = (data) => {
    if (data) patch(data);
    onChanged?.(data);
  };

  return (
    <>
      <div className="hidden overflow-x-auto md:block" data-testid={testid}>
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th key={c || `actions-${i}`}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <OpportunityTableRow
                key={row.id}
                row={row}
                compact={compact}
                onDialog={setDialog}
                onOverride={setOverrideRow}
                onValue={setValueRow}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden" data-testid={`${testid}-mobile`}>
        {rows.map((row) => (
          <OpportunityMobileCard
            key={row.id}
            row={row}
            open={expanded[row.id]}
            onToggle={() => setExpanded((s) => ({ ...s, [row.id]: !s[row.id] }))}
            onOverride={setOverrideRow}
          />
        ))}
      </div>

      <MarketValueDialog
        open={valueRow !== null}
        onOpenChange={(open) => !open && setValueRow(null)}
        loading={loading && valueRow !== null}
        row={
          valueRow
            ? {
                ...valueRow,
                ...(detail && detail.listing_id === valueRow.listing_id ? detail : {}),
              }
            : null
        }
        onApplied={(data) => {
          setValueRow(null);
          dialogChanged(data);
        }}
      />
      <BpmOverrideDialog
        open={overrideRow !== null}
        onOpenChange={(open) => !open && setOverrideRow(null)}
        row={overrideRow}
        onApplied={() => {
          setOverrideRow(null);
          onChanged?.();
        }}
      />
      <BpmDialog
        open={dialog.type === "bpm"}
        onOpenChange={close}
        bpm={detailFor(active)?.bpm}
        co2={detailFor(active)?.co2}
        listingId={active?.listing_id}
        onChanged={dialogChanged}
      />
      <ValuationDialog
        open={dialog.type === "valuation"}
        onOpenChange={close}
        valuation={detailFor(active)?.valuation}
        listingId={active?.listing_id}
        onChanged={dialogChanged}
      />
      <ScoreDialog
        open={dialog.type === "score"}
        onOpenChange={close}
        score={detailFor(active)?.deal_score}
      />
      <CostDialog
        open={dialog.type === "cost"}
        onOpenChange={close}
        costs={detailFor(active)?.costs}
        scenario={detailFor(active)?.scenarios?.base}
      />
    </>
  );
};
